import internalChatMessagesAPI from 'dashboard/api/internalChatMessages';

const state = {
  // keyed by channelId → array of messages
  byChannel: {},
  uiFlags: {
    isFetching: false,
    isSending: false,
  },
};

const getters = {
  getMessages: s => channelId => s.byChannel[channelId] || [],
  getUIFlags: s => s.uiFlags,
  getMessageById: s => messageId => {
    for (const messages of Object.values(s.byChannel)) {
      const found = messages.find(m => m.id === messageId);
      if (found) return found;
    }
    return null;
  },
  getThreadReplies: s => (channelId, parentId) => {
    const msgs = s.byChannel[channelId] || [];
    return msgs.filter(m => m.parent_id === parentId);
  },
};

const mutations = {
  SET_UI_FLAGS(s, flags) {
    s.uiFlags = { ...s.uiFlags, ...flags };
  },
  SET_MESSAGES(s, { channelId, messages, prepend = false }) {
    const existing = s.byChannel[channelId] || [];
    const merged = prepend ? [...messages, ...existing] : [...existing, ...messages];
    // deduplicate by id keeping last occurrence
    const seen = new Set();
    const deduped = [...merged].reverse().filter(m => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    }).reverse();
    s.byChannel = { ...s.byChannel, [channelId]: deduped };
  },
  ADD_MESSAGE(s, { channelId, message }) {
    const existing = s.byChannel[channelId] || [];
    if (existing.find(m => m.id === message.id)) return;
    s.byChannel = { ...s.byChannel, [channelId]: [...existing, message] };
  },
  UPDATE_MESSAGE(s, { channelId, message }) {
    const existing = s.byChannel[channelId] || [];
    s.byChannel = {
      ...s.byChannel,
      [channelId]: existing.map(m => (m.id === message.id ? { ...m, ...message } : m)),
    };
  },
  DELETE_MESSAGE(s, { channelId, messageId }) {
    const existing = s.byChannel[channelId] || [];
    s.byChannel = {
      ...s.byChannel,
      [channelId]: existing.filter(m => m.id !== messageId),
    };
  },
  ADD_REACTION(s, { channelId, messageId, reaction }) {
    const existing = s.byChannel[channelId] || [];
    s.byChannel = {
      ...s.byChannel,
      [channelId]: existing.map(m => {
        if (m.id !== messageId) return m;
        const reactions = [...(m.reactions || []), reaction];
        return { ...m, reactions };
      }),
    };
  },
  REMOVE_REACTION(s, { channelId, messageId, reactionId }) {
    const existing = s.byChannel[channelId] || [];
    s.byChannel = {
      ...s.byChannel,
      [channelId]: existing.map(m => {
        if (m.id !== messageId) return m;
        const reactions = (m.reactions || []).filter(r => r.id !== reactionId);
        return { ...m, reactions };
      }),
    };
  },
};

const actions = {
  async fetchMessages({ commit }, { channelId, before, after, limit } = {}) {
    commit('SET_UI_FLAGS', { isFetching: true });
    try {
      const params = {};
      if (before) params.before = before;
      if (after) params.after = after;
      if (limit) params.limit = limit;
      const { data } = await internalChatMessagesAPI.getMessages(channelId, params);
      // Backend returns { messages: [...], meta: {...} }
      const raw = data?.data || data || {};
      const messages = Array.isArray(raw) ? raw : raw.messages || [];
      const prepend = !!before;
      commit('SET_MESSAGES', { channelId, messages, prepend });
      return messages;
    } finally {
      commit('SET_UI_FLAGS', { isFetching: false });
    }
  },

  async sendMessage({ commit }, { channelId, data: msgData, content: directContent, files, parentId, echoId }) {
    commit('SET_UI_FLAGS', { isSending: true });
    try {
      const content = msgData?.content ?? directContent;
      const payload = { content };
      if (msgData?.parent_id || parentId) payload.parent_id = msgData?.parent_id || parentId;
      if (msgData?.echo_id || echoId) payload.echo_id = msgData?.echo_id || echoId;
      const { data } = await internalChatMessagesAPI.createMessage(channelId, payload, files || []);
      const message = data?.data || data;
      commit('ADD_MESSAGE', { channelId, message });
      return message;
    } finally {
      commit('SET_UI_FLAGS', { isSending: false });
    }
  },

  async updateMessage({ commit }, { channelId, messageId, data: msgData, content: directContent }) {
    const content = msgData?.content ?? directContent;
    const { data } = await internalChatMessagesAPI.updateMessage(channelId, messageId, { content });
    const message = data?.data || data;
    commit('UPDATE_MESSAGE', { channelId, message });
    return message;
  },

  async deleteMessage({ commit }, { channelId, messageId }) {
    await internalChatMessagesAPI.deleteMessage(channelId, messageId);
    commit('DELETE_MESSAGE', { channelId, messageId });
  },

  async fetchThread({ commit }, { channelId, messageId }) {
    const { data } = await internalChatMessagesAPI.getThread(channelId, messageId);
    // Backend returns { parent: {...}, replies: [...] }
    const raw = data?.data || data || {};
    const replies = Array.isArray(raw) ? raw : raw.replies || [];
    const parent = raw.parent || null;
    const messages = parent ? [parent, ...replies] : replies;
    commit('SET_MESSAGES', { channelId, messages });
    return { parent, replies };
  },

  async sendThreadReply({ commit }, { channelId, parentId, content, files }) {
    const payload = { content, parent_id: parentId };
    const { data } = await internalChatMessagesAPI.createMessage(channelId, payload, files || []);
    const message = data?.data || data;
    commit('ADD_MESSAGE', { channelId, message });
    return message;
  },

  async pinMessage(_ctx, { channelId, messageId }) {
    await internalChatMessagesAPI.pinMessage(channelId, messageId);
  },

  async unpinMessage(_ctx, { channelId, messageId }) {
    await internalChatMessagesAPI.unpinMessage(channelId, messageId);
  },

  async addReaction({ commit }, { channelId, messageId, emoji }) {
    const { data } = await internalChatMessagesAPI.addReaction(messageId, emoji);
    const reaction = data?.data || data;
    commit('ADD_REACTION', { channelId, messageId, reaction });
  },

  async removeReaction({ commit }, { channelId, messageId, reactionId }) {
    await internalChatMessagesAPI.removeReaction(messageId, reactionId);
    commit('REMOVE_REACTION', { channelId, messageId, reactionId });
  },

  // ActionCable handlers
  addMessageFromCable({ commit }, { channelId, message }) {
    commit('ADD_MESSAGE', { channelId, message });
  },
  updateMessageFromCable({ commit }, { channelId, message }) {
    commit('UPDATE_MESSAGE', { channelId, message });
  },
  deleteMessageFromCable({ commit }, { channelId, messageId }) {
    commit('DELETE_MESSAGE', { channelId, messageId });
  },
  addReactionFromCable({ commit }, { channelId, messageId, reaction }) {
    commit('ADD_REACTION', { channelId, messageId, reaction });
  },
  removeReactionFromCable({ commit }, { channelId, messageId, reactionId }) {
    commit('REMOVE_REACTION', { channelId, messageId, reactionId });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
